/**
 * Artifact List Display Component
 * 
 * Displays a list of child artifacts (e.g., Vision, Leadership, Strategy, etc.)
 * with clickable items to view details.
 */

import React from 'react';
import { CardSectionProps } from '../../../types/compositeCards';
import { 
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  RightOutlined,
  InfoCircleOutlined,
  StarFilled
} from '@ant-design/icons';

interface ArtifactListItem {
  id: string;
  name: string;
  status?: 'complete' | 'in-progress' | 'pending';
  description?: string;
  assessmentScore?: number; // 1-10 score for quick view
}

export const ArtifactListDisplay: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  const { data } = card;

  // Extract child artifacts from card data or use defaults for demo
  const childArtifacts: ArtifactListItem[] = (data as any).childArtifacts || [
    { id: '1', name: 'Vision', status: 'complete', description: 'Company vision and mission', assessmentScore: 8 },
    { id: '2', name: 'Leadership', status: 'complete', description: 'Leadership structure and roles', assessmentScore: 7 },
    { id: '3', name: 'Strategy', status: 'in-progress', description: 'Strategic planning and execution', assessmentScore: 6 },
    { id: '4', name: 'Guidance', status: 'complete', description: 'Operational guidance and policies', assessmentScore: 5 },
    { id: '5', name: 'Human Resources', status: 'pending', description: 'Team and talent management', assessmentScore: 4 }
  ];

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'complete':
        return <CheckCircleOutlined style={{ color: '#10b981', fontSize: '16px' }} />;
      case 'in-progress':
        return <ClockCircleOutlined style={{ color: '#f59e0b', fontSize: '16px' }} />;
      case 'pending':
        return <FileTextOutlined style={{ color: '#6b7280', fontSize: '16px' }} />;
      default:
        return <FileTextOutlined style={{ color: '#6b7280', fontSize: '16px' }} />;
    }
  };

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case 'complete': return '#10b981';
      case 'in-progress': return '#f59e0b';
      case 'pending': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 8) return '#10b981'; // Green - Excellent
    if (score >= 6) return '#f59e0b'; // Orange - Good
    return '#ef4444'; // Red - Needs Improvement
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 8) return 'Excellent';
    if (score >= 6) return 'Good';
    return 'Needs Work';
  };

  const handleArtifactClick = (artifact: ArtifactListItem) => {
    // Trigger action to open artifact card in the thread
    onAction?.('open-artifact-card', {
      artifactId: artifact.id,
      artifactName: artifact.name,
      artifactDescription: artifact.description,
      assessmentScore: artifact.assessmentScore
    });
  };

  return (
    <div style={{
      padding: '16px',
      borderTop: '1px solid #e5e7eb'
    }}>
      <h4 style={{
        margin: '0 0 12px 0',
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151'
      }}>
        Organization Components
      </h4>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        {childArtifacts.map((artifact) => (
          <div
            key={artifact.id}
            onClick={() => handleArtifactClick(artifact)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            {/* Status Icon */}
            <div style={{ marginRight: '12px' }}>
              {getStatusIcon(artifact.status)}
            </div>

            {/* Artifact Info */}
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#111827',
                marginBottom: '2px'
              }}>
                {artifact.name}
              </div>
              {artifact.description && (
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280'
                }}>
                  {artifact.description}
                </div>
              )}
            </div>

            {/* Assessment Score (if available) */}
            {artifact.assessmentScore !== undefined && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginRight: '12px'
              }}>
                <StarFilled style={{ 
                  color: getScoreColor(artifact.assessmentScore),
                  fontSize: '14px'
                }} />
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end'
                }}>
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: getScoreColor(artifact.assessmentScore)
                  }}>
                    {artifact.assessmentScore}
                  </span>
                  <span style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {getScoreLabel(artifact.assessmentScore)}
                  </span>
                </div>
              </div>
            )}

            {/* Arrow Icon */}
            <RightOutlined style={{ 
              color: '#9ca3af',
              fontSize: '12px'
            }} />
          </div>
        ))}
      </div>

      {/* Info Note */}
      <div style={{
        marginTop: '12px',
        padding: '8px 12px',
        backgroundColor: '#eff6ff',
        borderLeft: '3px solid #3b82f6',
        borderRadius: '4px'
      }}>
        <p style={{
          margin: 0,
          fontSize: '12px',
          color: '#1e40af',
          lineHeight: '1.4'
        }}>
          <InfoCircleOutlined style={{ marginRight: '6px' }} />
          These components contribute to your overall organization score. Click any item to view detailed assessment.
        </p>
      </div>
    </div>
  );
};

